import { View } from "react-native"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import Tooltips from "@/constants/ToolTips"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { type LoanApplicationInput } from "@/generated/graphql"
import { Divider } from "@/screens/loans/components/Divider"
import { BFColors } from "@/constants/Colors"
import { useEffect } from "react"

export const PropertyInfoSummary = ({
  showEditButton,
  readOnly = false,
  showErrors = false,
  onProfitChange,
}: {
  onProfitChange?: (profit: number) => void
  showEditButton?: boolean
  readOnly?: boolean
  showErrors?: boolean
}) => {
  const { loanApplication: loan, update } = useLoanContext()

  const area = loan.property?.area
  const isRefi = loan.contractStatus === "refinance"

  function onValueChangeFactory(prop: keyof LoanApplicationInput) {
    if (readOnly) {
      return undefined
    }

    return (val: number) => {
      update({ [prop]: val })
    }
  }

  const costBasis =
    (loan.purchasePrice || 0) +
    (loan.rehabEstimate || 0) +
    (isRefi ? loan.constructionCostsSpent || 0 : 0)
  const grossProfit = (loan.afterRepairValue || 0) - costBasis

  useEffect(() => {
    if (onProfitChange) {
      onProfitChange(grossProfit)
    }
  }, [grossProfit])

  return (
    <View className="flex flex-col w-full">
      <CalculatorItem
        testId={"estimated-arv-input"}
        title="Estimated ARV"
        subtitle={
          area && loan.afterRepairValue
            ? `($${Math.round(loan.afterRepairValue / area)}/Sq Ft)`
            : null
        }
        value={loan.afterRepairValue || 0}
        onValueChange={onValueChangeFactory("afterRepairValue")}
        tooltip={Tooltips.estimatedARV}
        isError={showErrors && !loan.afterRepairValue}
      />
      <CalculatorItem
        testId={"purchase-price-input"}
        title={isRefi ? "Original Purchase Price" : "Purchase Price"}
        subtitle={
          area && loan.purchasePrice
            ? `($${Math.round(loan.purchasePrice / area)}/Sq Ft)`
            : null
        }
        value={loan.purchasePrice}
        onValueChange={onValueChangeFactory("purchasePrice")}
        tooltip={isRefi ? Tooltips.purchasePriceRefi : Tooltips.purchasePrice}
        isError={showErrors && !loan.purchasePrice}
      />

      {isRefi && (
        <CalculatorItem
          title={"Estimated As-is-Value"}
          value={loan.estimatedValue}
          onValueChange={onValueChangeFactory("estimatedValue")}
          tooltip={Tooltips.estimatedAsIsValue}
        />
      )}

      {isRefi && (
        <CalculatorItem
          title={"Existing Loan Balance"}
          value={loan.existingLoanAmount}
          onValueChange={onValueChangeFactory("existingLoanAmount")}
          tooltip={Tooltips.existingLoanBalance}
        />
      )}

      {!isRefi && (
        <CalculatorItem
          title="Earnest $ Deposit"
          value={loan.earnestMoneyDeposit}
          onValueChange={onValueChangeFactory("earnestMoneyDeposit")}
          // tooltip={Tooltips.setEarnestMoneyDeposit} // Exact copy TBD
        />
      )}

      <Divider />

      {isRefi && (
        <CalculatorItem
          title={"Construction $ Spent"}
          value={loan.constructionCostsSpent}
          onValueChange={onValueChangeFactory("constructionCostsSpent")}
          tooltip={Tooltips.constructionSpent}
        />
      )}

      <CalculatorItem
        title="Estimated Rehab Costs"
        subtitle={
          area && loan.rehabEstimate
            ? `($${Math.round(loan.rehabEstimate / area)}/Sq Ft)`
            : null
        }
        value={loan.rehabEstimate}
        onValueChange={onValueChangeFactory("rehabEstimate")}
        tooltip={Tooltips.estimatedRehabCosts}
      />

      <Divider />

      <CalculatorItem
        title="Cost Basis"
        value={costBasis}
        tooltip={Tooltips.costBasis}
        noMin
      />

      <CalculatorItem
        title="Gross Potential Profit"
        value={Math.abs(grossProfit)}
        textColor={grossProfit >= 0 ? BFColors.calmBlue : BFColors.redOrange}
        prefix={grossProfit >= 0 ? "$" : "-$"}
        tooltip={Tooltips.grossProfitPotential}
        noMin
      />
      <CalculatorItem
        title="Hold Period (mo)"
        value={loan.holdPeriod}
        prefix={""}
        onValueChange={onValueChangeFactory("holdPeriod")}
        tooltip={Tooltips.holdPeriod}
      />
    </View>
  )
}
